/**
 * AddressSearch - Component for searching and selecting addresses
 * Uses Google Places API (New) for accurate Swedish address data
 * Returns structured address data (street, postalCode, city, country)
 */

import { useState, useRef, useEffect } from 'react'
import { Search, MapPin, X, Loader2, AlertCircle } from 'lucide-react'

const GOOGLE_API_KEY = import.meta.env.VITE_GOOGLE_PLACES_API_KEY

interface GooglePlaceSuggestion {
  placePrediction: {
    placeId: string
    text: {
      text: string
    }
    structuredFormat?: {
      mainText: { text: string }
      secondaryText: { text: string }
    }
  }
}

interface GooglePlaceDetails {
  displayName?: { text: string }
  formattedAddress?: string
  addressComponents?: Array<{
    longText: string
    shortText: string
    types: string[]
  }>
  location?: {
    latitude: number
    longitude: number
  }
}

interface StructuredAddress {
  street: string
  postalCode: string
  city: string
  country: string
}

interface AddressSearchProps {
  onSelect: (address: StructuredAddress) => void
  buttonClassName?: string
}

export function AddressSearch({ onSelect, buttonClassName = '' }: AddressSearchProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [suggestions, setSuggestions] = useState<GooglePlaceSuggestion[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const searchTimeoutRef = useRef<NodeJS.Timeout>()
  const inputRef = useRef<HTMLInputElement>(null)

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    if (searchTerm.length < 3) {
      setSuggestions([])
      setError(null)
      return
    }

    searchTimeoutRef.current = setTimeout(async () => {
      await searchAddresses(searchTerm)
    }, 300)

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [searchTerm])

  const searchAddresses = async (query: string) => {
    if (!GOOGLE_API_KEY) {
      setError('Google API-nyckel saknas. Kontakta administratör.')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const response = await fetch(
        'https://places.googleapis.com/v1/places:autocomplete',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Goog-Api-Key': GOOGLE_API_KEY
          },
          body: JSON.stringify({
            input: query,
            includedRegionCodes: ['se'],
            languageCode: 'sv'
          })
        }
      )

      if (!response.ok) {
        throw new Error(`Google API error: ${response.status}`)
      }

      const data = await response.json()
      setSuggestions(data.suggestions || [])
    } catch (err) {
      console.error('Error searching addresses:', err)
      setError('Kunde inte söka adresser. Försök igen.')
      setSuggestions([])
    } finally {
      setIsLoading(false)
    }
  }

  const handleSelect = async (suggestion: GooglePlaceSuggestion) => {
    if (!GOOGLE_API_KEY) return

    const placeId = suggestion.placePrediction.placeId

    try {
      // Get place details
      const response = await fetch(
        `https://places.googleapis.com/v1/places/${placeId}`,
        {
          headers: {
            'X-Goog-Api-Key': GOOGLE_API_KEY,
            'X-Goog-FieldMask': 'displayName,formattedAddress,addressComponents,location'
          }
        }
      )

      if (!response.ok) {
        throw new Error(`Google API error: ${response.status}`)
      }

      const place: GooglePlaceDetails = await response.json()

      // Parse address components
      let street = ''
      let streetNumber = ''
      let postalCode = ''
      let city = ''
      let country = 'Sverige'

      if (place.addressComponents) {
        for (const component of place.addressComponents) {
          if (component.types.includes('street_number')) {
            streetNumber = component.longText
          } else if (component.types.includes('route')) {
            street = component.longText
          } else if (component.types.includes('postal_code')) {
            postalCode = component.longText
          } else if (component.types.includes('postal_town') || component.types.includes('locality')) {
            city = component.longText
          } else if (component.types.includes('country')) {
            country = component.longText
          }
        }
      }

      // Combine street and number (Swedish format: "Gatan 5")
      const fullStreet = streetNumber ? `${street} ${streetNumber}` : street

      const structuredAddress: StructuredAddress = {
        street: fullStreet,
        postalCode,
        city,
        country
      }

      console.log('Google place data:', place)
      console.log('Parsed address:', structuredAddress)

      onSelect(structuredAddress)
    } catch (err) {
      console.error('Error getting place details:', err)
      // Fallback: use the suggestion text
      onSelect({
        street: suggestion.placePrediction.structuredFormat?.mainText.text || suggestion.placePrediction.text.text,
        postalCode: '',
        city: suggestion.placePrediction.structuredFormat?.secondaryText.text || '',
        country: 'Sverige'
      })
    }

    setIsOpen(false)
    setSearchTerm('')
    setSuggestions([])
  }

  if (!isOpen) {
    return (
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:hover:text-blue-300 dark:hover:bg-blue-900/20 rounded-lg transition-colors ${buttonClassName}`}
      >
        <Search className="h-4 w-4" />
        Sök adress i Sverige
      </button>
    )
  }

  return (
    <div className="relative">
      {/* Search input */}
      <div className="flex items-center gap-2 mb-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-field pl-10 pr-4"
            placeholder="Sök adress med gatunamn och nummer..."
          />
        </div>
        <button
          type="button"
          onClick={() => {
            setIsOpen(false)
            setSearchTerm('')
            setSuggestions([])
            setError(null)
          }}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-nature-700 rounded-lg transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-400 text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Results dropdown */}
      {(isLoading || suggestions.length > 0 || (searchTerm.length >= 3 && !error)) && (
        <div className="absolute z-50 w-full bg-[#faf9f7] dark:bg-nature-800 border border-nature-200 dark:border-nature-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {isLoading ? (
            <div className="p-3 text-center text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin mx-auto mb-1" />
              <span className="text-sm">Söker...</span>
            </div>
          ) : suggestions.length > 0 ? (
            <div className="py-1">
              {suggestions.map((item, index) => (
                <button
                  key={item.placePrediction.placeId || index}
                  type="button"
                  onClick={() => handleSelect(item)}
                  className="w-full px-3 py-2 text-left hover:bg-nature-50 dark:hover:bg-nature-700 flex items-start gap-2"
                >
                  <MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-nature-900 dark:text-nature-100">
                      {item.placePrediction.structuredFormat?.mainText.text || item.placePrediction.text.text}
                    </div>
                    {item.placePrediction.structuredFormat?.secondaryText && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {item.placePrediction.structuredFormat.secondaryText.text}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          ) : searchTerm.length >= 3 && !error ? (
            <div className="p-3 text-center text-gray-500">
              <Search className="h-4 w-4 mx-auto mb-1" />
              <div className="text-sm">Inga resultat hittades</div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}
